/**
 * The route table for the public website (§4w) — one module, three consumers.
 *
 * ## Why it is data rather than JSX
 *
 * §4w asks for a generated `sitemap.xml` and gives the reason: "A page cannot be added and
 * forgotten, which is the only failure mode a hand-written sitemap has." That only holds if the
 * router, the prerenderer and the sitemap all read the **same** list, so the list is here and
 * all three read it:
 *
 *   - `MarketingApp` registers `PATTERNS` with React Router.
 *   - `tools/prerender.mjs` renders every entry of `MARKETING_URLS` to a file.
 *   - the same array becomes `sitemap.xml`, and the tests walk it.
 *
 * ## English at the root, Arabic under `/ar` (D428)
 *
 * §4w makes this a **different rule** from §4i on purpose. Inside the application the language
 * follows the person; on the public website it follows the URL, because a search engine indexes
 * URLs and two languages sharing one URL means one of them is never indexed — and because a link
 * somebody sends a colleague must arrive in the language it was sent in.
 *
 * English stays unprefixed rather than moving to `/en`: the shorter form is the one that will be
 * printed and pasted. `/en/...` redirects to it permanently for anybody who guesses.
 */

import type { Language } from '@/shared/i18n/language'

/**
 * The eleven kinds of page. One component each; two of them take a slug, and three of them —
 * `privacy`, `terms`, `refunds` — share `LegalPage`.
 */
export type MarketingPageKey =
  | 'landing'
  | 'features'
  | 'pricing'
  | 'industries'
  | 'industry'
  | 'useCases'
  | 'useCase'
  | 'contact'
  | 'privacy'
  | 'terms'
  | 'refunds'

/**
 * The eight sectors (§4w).
 *
 * Each is a page because the *problem* differs even though the product does not — "not a
 * template with the noun swapped". A sector that cannot be written about specifically is not on
 * this list, which is why it is eight and not eighteen.
 */
export const INDUSTRY_SLUGS = [
  'clinics',
  'banks',
  'government',
  'telecom',
  'retail',
  'education',
  'salons',
  'vehicle-services',
] as const

export type IndustrySlug = (typeof INDUSTRY_SLUGS)[number]

/** The six jobs (§4w), organised by the job rather than by the buyer. */
export const USE_CASE_SLUGS = [
  'walk-in-queue',
  'appointment-booking',
  'self-check-in-kiosk',
  'waiting-room-screen',
  'multi-branch',
  'fewer-no-shows',
] as const

export type UseCaseSlug = (typeof USE_CASE_SLUGS)[number]

/** One concrete, English-rooted path. `param` is the slug for the two nested kinds. */
export interface MarketingPath {
  readonly key: MarketingPageKey
  readonly path: string
  readonly param?: string
}

/**
 * Every marketing page, as the path it has in English.
 *
 * Order matters only in one place — the sitemap and the footer read it top to bottom — so it is
 * written in the order §4w lists them rather than alphabetically.
 */
export const MARKETING_PATHS: readonly MarketingPath[] = [
  { key: 'landing', path: '/' },
  { key: 'features', path: '/features' },
  { key: 'pricing', path: '/pricing' },
  { key: 'industries', path: '/industries' },
  ...INDUSTRY_SLUGS.map((slug) => ({
    key: 'industry' as const,
    path: `/industries/${slug}`,
    param: slug,
  })),
  { key: 'useCases', path: '/use-cases' },
  ...USE_CASE_SLUGS.map((slug) => ({
    key: 'useCase' as const,
    path: `/use-cases/${slug}`,
    param: slug,
  })),
  { key: 'contact', path: '/contact' },
  { key: 'privacy', path: '/legal/privacy' },
  { key: 'terms', path: '/legal/terms' },
  { key: 'refunds', path: '/legal/refunds' },
]

/**
 * The router patterns, one per page kind.
 *
 * Registered twice by `MarketingApp` — bare and under `/ar` — rather than being listed twice
 * here, so a new page is one line in `MARKETING_PATHS` and one line here and cannot be added to
 * one language only.
 */
export const PATTERNS: readonly { key: MarketingPageKey; pattern: string }[] = [
  { key: 'landing', pattern: '/' },
  { key: 'features', pattern: '/features' },
  { key: 'pricing', pattern: '/pricing' },
  { key: 'industries', pattern: '/industries' },
  { key: 'industry', pattern: '/industries/:slug' },
  { key: 'useCases', pattern: '/use-cases' },
  { key: 'useCase', pattern: '/use-cases/:slug' },
  { key: 'contact', pattern: '/contact' },
  { key: 'privacy', pattern: '/legal/privacy' },
  { key: 'terms', pattern: '/legal/terms' },
  { key: 'refunds', pattern: '/legal/refunds' },
]

/** The prefix a language's pages sit under. English has none. */
export const LANGUAGE_PREFIX: Record<Language, string> = {
  en: '',
  ar: '/ar',
}

/**
 * The URL a page has in a given language.
 *
 * `/` is the one case worth spelling out: Arabic's landing page is `/ar`, not `/ar/`, because a
 * trailing slash and its absence are two URLs to a crawler and only one of them can be canonical.
 */
export function localisedPath(language: Language, path: string): string {
  const prefix = LANGUAGE_PREFIX[language]
  if (path === '/') {
    return prefix === '' ? '/' : prefix
  }
  return `${prefix}${path}`
}

/**
 * The first path segments the public website owns (§4ac, D545).
 *
 * A path starting with one of these that is not a page is the website's **not-found page**, served
 * by the API with a real 404 — so the browser has to mount this tree for it too, or the application
 * would replace the prerendered page with its own. Every word here is reserved, so no tenant can be
 * shadowed; a bare unknown word outside it still has the shape of a tenant slug and goes to the
 * application.
 *
 * The same list is `TenantSlug.PublicWebsiteSegments` in C# and `WEBSITE_SEGMENTS` in
 * `tools/servePrerendered.mjs`; `websiteSegments.test.ts` keeps the three equal.
 */
export const WEBSITE_SEGMENTS: readonly string[] = [
  'ar',
  'contact',
  'features',
  'industries',
  'legal',
  'pricing',
  'use-cases',
  'about',
  'blog',
  'careers',
  'integrations',
  'news',
  'partners',
  'press',
  'privacy',
  'security',
  'terms',
]

/** What `resolveMarketingEntry` answers for a URL that is a public page. */
export interface MarketingEntry {
  /** The language the URL is in. */
  readonly language: Language
  /** The English path underneath the prefix — the key into `MARKETING_PATHS`. */
  readonly path: string
  /** The URL this page should actually be at, which differs only for an `/en/...` request. */
  readonly pathname: string
  /** True when the request used the `/en` prefix, which redirects to the root form (§4w). */
  readonly redirected: boolean
  /** True for a path the website owns and has no page for: its not-found page (§4ac, D545). */
  readonly notFound?: boolean
}

/**
 * Whether a URL is a public page, and if so which one.
 *
 * Used by `main.tsx` to decide which of the two trees to mount, so it has to answer for **every**
 * path in the product and not only for these. It is an exact match against the route table on
 * purpose: `/pricing` answers, `/bright-care/dashboard` does not, and a tenant that somehow holds a
 * reserved slug still reaches the application rather than a marketing page — the reserved-word list
 * and its startup check are what stop that arising in the first place.
 */
export function resolveMarketingEntry(pathname: string): MarketingEntry | null {
  const trimmed = stripTrailingSlash(pathname)

  // `/en` and `/en/...` are accepted and then corrected, rather than falling through to the
  // application and rendering its not-found page. Somebody who guesses the prefixed form has
  // guessed a URL that used to be plausible, and the honest answer is a redirect.
  const english = trimmed === '/en' || trimmed.startsWith('/en/')
  const arabic = trimmed === '/ar' || trimmed.startsWith('/ar/')

  const language: Language = arabic ? 'ar' : 'en'
  const prefix = english ? '/en' : arabic ? '/ar' : ''
  const path = prefix === '' ? trimmed : trimmed.slice(prefix.length) || '/'

  if (!MARKETING_PATHS.some((entry) => entry.path === path)) {
    // Owned by the website but not a page: still this tree, so the prerendered not-found page is
    // hydrated rather than replaced. `/en/...` is not here — it redirects before it is ever a 404.
    const first = trimmed.split('/')[1] ?? ''
    return !english && WEBSITE_SEGMENTS.includes(first)
      ? { language, path, pathname: trimmed, redirected: false, notFound: true }
      : null
  }

  return {
    language,
    path,
    pathname: localisedPath(language, path),
    redirected: english,
  }
}

/** `/pricing/` → `/pricing`, and `/` → `/`. */
export function stripTrailingSlash(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.replace(/\/+$/, '') : pathname
}

/** Every public URL on the site, both languages — what the prerenderer and the sitemap read. */
export const MARKETING_URLS: readonly { language: Language; url: string; entry: MarketingPath }[] =
  (['en', 'ar'] as const).flatMap((language) =>
    MARKETING_PATHS.map((entry) => ({
      language,
      url: localisedPath(language, entry.path),
      entry,
    })),
  )
